import { Box, Divider, MenuItem, Slider, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  selectViewedGroupEdges,
  selectViewedGroups,
  selectViewedRectangles,
} from "@store/selectors";
import {
  addGroupEdge,
  deleteGroupEdge,
  flipGroupEdge,
  setGroupEdgeCredence,
  setGroupEdgeKind,
} from "@store/slices/workspaceSlice";
import { closeGroupEdgeContextMenu } from "@features/annogram/canvas/state/canvasAuxSlice";
import ContextMenu from "@shared/components/ContextMenu";
import type { ArrowKind } from "@/types/canvasTypes";

// Mirrors the arrow menu's endpoint labeling: a short slice of the
// thing's own text so the direction entries read as real statements.
function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}
function labelFor(text: string | undefined, fallback: string): string {
  const t = (text ?? "").trim();
  return t ? truncate(t, 12) : fallback;
}

/**
 * Right-click menu for a group edge. Mirrors the arrow menu's controls
 * (kind, credence, delete) using the shared ContextMenu shell.
 */
const GroupEdgeContextMenu = () => {
  const dispatch = useAppDispatch();
  const target = useAppSelector((state) => state.canvasAux.groupEdgeContextMenu);
  const coords = useAppSelector((state) => state.canvasAux.coords);
  const edges = useAppSelector(selectViewedGroupEdges);
  const groups = useAppSelector(selectViewedGroups);
  const rectangles = useAppSelector(selectViewedRectangles);

  if (!target) return null;
  const edge = edges.find(
    (e) =>
      e.fromGroupId === target.fromGroupId &&
      e.toGroupId === target.toGroupId &&
      e.toRectIdx === target.toRectIdx
  );
  if (!edge) return null;

  const closeMenu = () => dispatch(closeGroupEdgeContextMenu());

  // Endpoint labels, mirroring the arrow menu: source is the group's
  // name, target is the other group's name or the node's own text.
  const sourceLabel = labelFor(
    groups.find((g) => g.id === target.fromGroupId)?.name,
    "this group"
  );
  const targetLabel =
    target.toGroupId !== undefined
      ? labelFor(
          groups.find((g) => g.id === target.toGroupId)?.name,
          "other group"
        )
      : labelFor(
          target.toRectIdx !== undefined
            ? rectangles[target.toRectIdx]?.tag ||
                rectangles[target.toRectIdx]?.text
            : undefined,
          "node"
        );

  const check = (on: boolean) => (on ? "\u2713 " : "\u2003");
  const setKind = (kind: ArrowKind) => {
    dispatch(setGroupEdgeKind({ ...target, kind }));
    closeMenu();
  };

  // Reverse-direction and both-directed only exist for group-to-group
  // edges: a node-targeted edge has no representable reverse (edges
  // originate from groups only), which is also why the rect-arrow
  // menu's pair options have no node-target equivalent here.
  const isGroupTarget = target.toGroupId !== undefined;

  const menuButtons = [
    {
      text: `${check(edge.kind === "undirected")}Undirected`,
      onClick: () => setKind("undirected"),
    },
    {
      text: `${check(edge.kind === "directed")}Directed \u2192 ${targetLabel}`,
      onClick: () => setKind("directed"),
    },
    ...(isGroupTarget
      ? [
          {
            text: `${check(false)}Directed \u2192 ${sourceLabel}`,
            onClick: () => {
              dispatch(
                flipGroupEdge({
                  fromGroupId: target.fromGroupId,
                  toGroupId: target.toGroupId!,
                })
              );
              closeMenu();
            },
          },
          {
            text: `${check(false)}Both directed (circular)`,
            onClick: () => {
              // Mirror the rect-arrow affordance: current edge becomes
              // directed, and an opposing directed edge is added with
              // the same credence/tag (addGroupEdge dedupes if present).
              dispatch(setGroupEdgeKind({ ...target, kind: "directed" }));
              dispatch(
                addGroupEdge({
                  fromGroupId: target.toGroupId!,
                  toGroupId: target.fromGroupId,
                  kind: "directed",
                  credence: edge.credence,
                  tag: edge.tag,
                })
              );
              closeMenu();
            },
          },
        ]
      : []),
    {
      text: `${check(edge.kind === "bidirectional")}Bidirectional (\u2194)`,
      onClick: () => setKind("bidirectional"),
    },
    {
      text: "Delete",
      onClick: () => {
        dispatch(deleteGroupEdge(target));
        closeMenu();
      },
    },
  ];

  const handleCredenceChange = (_: Event, value: number | number[]) => {
    const credence = Array.isArray(value) ? (value[0] ?? 100) : value;
    dispatch(setGroupEdgeCredence({ ...target, credence }));
  };

  return (
    <ContextMenu
      open={true}
      menuCoords={coords}
      menuButtons={menuButtons}
      onClose={closeMenu}
    >
      <Divider />
      <MenuItem
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        sx={{ flexDirection: "column", alignItems: "stretch" }}
        disableRipple
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            width: "100%",
          }}
        >
          <Typography variant="body1">Credence</Typography>
          <Typography variant="body1" color="text.secondary">
            {edge.credence ?? 100}%
          </Typography>
        </Box>
        <Slider
          size="small"
          value={edge.credence ?? 100}
          onChange={handleCredenceChange}
          min={1}
          max={100}
          step={1}
          sx={{ width: 180, mt: -0.5, alignSelf: "center" }}
          aria-label="Credence"
        />
      </MenuItem>
    </ContextMenu>
  );
};

export default GroupEdgeContextMenu;
